import { FaceDetector, FilesetResolver } from '@mediapipe/tasks-vision';

export const CUSTOMER_ID_DOCS_GCS_BASE = 'https://storage.googleapis.com/docs_tcdghgcfycdtycgdc';

/** Fallback public URL when a signed view URL is unavailable. */
export const customerIdDocPublicUrl = (fileId?: string | null) => {
  if (!fileId) return '';
  if (fileId.startsWith('http')) return fileId;
  return `${CUSTOMER_ID_DOCS_GCS_BASE}/${fileId}`;
};

/**
 * Reuse the same signed GET URL until near expiry so <img> can hit browser HTTP cache.
 * Fresh signatures on every /customer-iddoc call defeat Cache-Control on the object.
 */
type IdDocViewCacheEntry = { viewUrl: string; expiresAt: number };
const idDocViewCache = new Map<string, IdDocViewCacheEntry>();
/** Server signs for 60m — drop a bit early so we never hand the UI an expired URL. */
const ID_DOC_VIEW_CACHE_TTL_MS = 50 * 60 * 1000;

export const getCachedIdDocViewUrl = (fileId?: string | null): string | null => {
  if (!fileId || fileId.startsWith('http')) return null;
  const entry = idDocViewCache.get(fileId);
  if (!entry) return null;
  if (Date.now() >= entry.expiresAt) {
    idDocViewCache.delete(fileId);
    return null;
  }
  return entry.viewUrl;
};

export const setCachedIdDocViewUrl = (
  fileId?: string | null,
  viewUrl?: string | null,
) => {
  if (!fileId || fileId.startsWith('http') || !viewUrl) return;
  idDocViewCache.set(fileId, {
    viewUrl,
    expiresAt: Date.now() + ID_DOC_VIEW_CACHE_TTL_MS,
  });
};

/** Cropped faces are tiny data URLs — cache by file id so re-selecting a guest is instant. */
const idDocFaceCache = new Map<string, string>();

export const getCachedIdDocFace = (fileId?: string | null): string | null => {
  if (!fileId || fileId.startsWith('http')) return null;
  return idDocFaceCache.get(fileId) || null;
};

export const setCachedIdDocFace = (
  fileId?: string | null,
  faceDataUrl?: string | null,
) => {
  if (!fileId || fileId.startsWith('http') || !faceDataUrl) return;
  idDocFaceCache.set(fileId, faceDataUrl);
};

const fileToDataUrl = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result as string);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const isHeicLike = (file: File) => {
  const name = file.name.toLowerCase();
  const type = (file.type || '').toLowerCase();
  return type.includes('heic')
    || type.includes('heif')
    || name.endsWith('.heic')
    || name.endsWith('.heif');
};

const isPdfFile = (file: File) => (
  file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
);

/** Extra margin around the detected face so the ID portrait crop looks natural. */
const FACE_CROP_PAD = 0.55;

const FACE_FAIL_MSG = 'Could not extract a face from this ID. Use a clear front-facing photo of the document.';

// Same-origin copies in /public/mediapipe. jsdelivr is often unreachable and
// surfaces as a delayed "Failed to fetch" on the reserve form.
const mediapipeAssetOrigin = (): string => {
  const publicUrl = process.env.PUBLIC_URL || '';
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}${publicUrl}`;
  }
  return publicUrl;
};
const WASM_ROOT = `${mediapipeAssetOrigin()}/mediapipe/wasm`;
const FACE_MODEL = `${mediapipeAssetOrigin()}/mediapipe/blaze_face_short_range.tflite`;

const FACE_ASSETS_FAIL_MSG = 'Could not load face detection. Try again.';

const isNetworkFetchError = (err: unknown): boolean => {
  if (!(err instanceof Error)) return false;
  const msg = err.message || '';
  return msg === 'Failed to fetch'
    || msg === 'Load failed'
    || msg === 'NetworkError when attempting to fetch resource.'
    || msg.includes('NetworkError');
};

const toFaceExtractError = (err: unknown, fallback: string): Error => {
  if (isNetworkFetchError(err)) return new Error(FACE_ASSETS_FAIL_MSG);
  if (err instanceof Error && err.name === 'AbortError') return new Error(fallback);
  if (err instanceof Error && err.message) return err;
  return new Error(fallback);
};

/** Singleton — first load pays for wasm+model; later ID uploads reuse it. */
let faceDetectorPromise: Promise<FaceDetector> | null = null;
/** True only after wasm+model finished loading at least once this page session. */
let faceDetectorReady = false;

/** Shown only on the cold first extract — later runs reuse the in-memory detector. */
export const FACE_EXTRACT_SLOW_FIRST_NOTICE = 'First face detection can take a few seconds while the model loads.';

export const FACE_EXTRACT_DETECTING_LABEL = 'Detecting face in document…';

export const FACE_EXTRACT_DETECTING_SLOW_LABEL = 'Loading face model (first time)…';

/** Whether MediaPipe is already warm for this tab (fast extracts). */
export const isIdDocFaceDetectorReady = (): boolean => faceDetectorReady;

/**
 * Only warn about a slow first extract when the model is still cold
 * and we are not serving a cached face crop.
 */
export const shouldNotifySlowFirstFaceExtract = (opts: {
  detectorReady: boolean;
  faceCached?: boolean;
}): boolean => !opts.detectorReady && !opts.faceCached;

/** True when the next MediaPipe run must download/init the model. */
export const willNextFaceExtractBeSlowFirstLoad = (): boolean => (
  shouldNotifySlowFirstFaceExtract({ detectorReady: faceDetectorReady })
);

const getFaceDetector = (): Promise<FaceDetector> => {
  if (!faceDetectorPromise) {
    faceDetectorPromise = (async () => {
      const vision = await FilesetResolver.forVisionTasks(WASM_ROOT);
      const detector = await FaceDetector.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: FACE_MODEL,
          // Prefer GPU when available; falls back silently.
          delegate: 'GPU',
        },
        runningMode: 'IMAGE',
        minDetectionConfidence: 0.5,
        minSuppressionThreshold: 0.3,
      });
      faceDetectorReady = true;
      return detector;
    })().catch((err) => {
      // Allow a later retry if the first init failed (network/wasm).
      faceDetectorPromise = null;
      faceDetectorReady = false;
      throw toFaceExtractError(err, FACE_ASSETS_FAIL_MSG);
    });
  }
  return faceDetectorPromise;
};

const loadImageElement = (src: string): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error(FACE_FAIL_MSG));
  img.src = src;
});

type FaceBox = {
  originX: number;
  originY: number;
  width: number;
  height: number;
};

/** Pick the largest face — on IDs that is usually the portrait photo, not a tiny hologram face. */
const pickLargestBox = (boxes: FaceBox[]): FaceBox | null => {
  if (!boxes.length) return null;
  return boxes.reduce((best, box) => (
    (box.width * box.height) > (best.width * best.height) ? box : best
  ));
};

/** Crop a padded square-ish portrait from the face bounding box onto a JPEG data URL. */
const cropFaceToDataUrl = (img: HTMLImageElement, box: FaceBox): string => {
  const padX = box.width * FACE_CROP_PAD;
  const padY = box.height * FACE_CROP_PAD;
  let x = Math.floor(box.originX - padX);
  let y = Math.floor(box.originY - padY);
  let w = Math.ceil(box.width + padX * 2);
  let h = Math.ceil(box.height + padY * 2);

  // Clamp to image bounds.
  x = Math.max(0, x);
  y = Math.max(0, y);
  w = Math.min(w, img.naturalWidth - x);
  h = Math.min(h, img.naturalHeight - y);
  if (w < 8 || h < 8) {
    throw new Error(FACE_FAIL_MSG);
  }

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error(FACE_FAIL_MSG);
  ctx.drawImage(img, x, y, w, h, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', 0.92);
};

/**
 * Run MediaPipe Face Detector on an ID image and return a cropped portrait data URL.
 * Throws when no usable face is found (caller must hide both previews).
 */
export const extractIdDocFaceDataUrl = async (imageSrc: string): Promise<string> => {
  const img = await loadImageElement(imageSrc);
  const detector = await getFaceDetector();
  const result = detector.detect(img);
  const boxes: FaceBox[] = [];
  (result.detections || []).forEach((d) => {
    const b = d.boundingBox;
    if (
      b
      && Number.isFinite(b.originX)
      && Number.isFinite(b.originY)
      && Number.isFinite(b.width)
      && Number.isFinite(b.height)
      && b.width > 0
      && b.height > 0
    ) {
      boxes.push({
        originX: b.originX,
        originY: b.originY,
        width: b.width,
        height: b.height,
      });
    }
  });
  const best = pickLargestBox(boxes);
  if (!best) throw new Error(FACE_FAIL_MSG);
  return cropFaceToDataUrl(img, best);
};

/**
 * Fetch a remote/signed ID image as a blob first so the canvas crop is not CORS-tainted.
 * Optional cacheKey (GCS file id) skips MediaPipe on repeat views of the same doc.
 */
export const extractIdDocFaceFromViewUrl = async (
  viewUrl: string,
  cacheKey?: string | null,
): Promise<string> => {
  const cached = getCachedIdDocFace(cacheKey);
  if (cached) return cached;

  // Local data/blob URLs can go straight through the detector.
  if (viewUrl.startsWith('data:') || viewUrl.startsWith('blob:')) {
    const face = await extractIdDocFaceDataUrl(viewUrl);
    setCachedIdDocFace(cacheKey, face);
    return face;
  }

  let res: Response;
  try {
    // Bound the GCS GET so a hung CORS/preflight cannot stall the reserve form.
    const abort = new AbortController();
    const abortTimer = setTimeout(() => abort.abort(), 12000);
    try {
      res = await fetch(viewUrl, { signal: abort.signal });
    } finally {
      clearTimeout(abortTimer);
    }
  } catch (err) {
    throw toFaceExtractError(err, FACE_FAIL_MSG);
  }
  if (!res.ok) throw new Error(FACE_FAIL_MSG);
  const blob = await res.blob();
  // Reject obvious non-images (e.g. PDF) before spinning up MediaPipe.
  if (blob.type && blob.type.includes('pdf')) {
    throw new Error(FACE_FAIL_MSG);
  }
  const objectUrl = URL.createObjectURL(blob);
  try {
    const face = await extractIdDocFaceDataUrl(objectUrl);
    setCachedIdDocFace(cacheKey, face);
    return face;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

/** Read an ID document file for local preview/upload and require a detectable face on photos. */
export const readIdDocumentFile = async (file: File): Promise<{
  documentDataUrl: string;
  faceDataUrl: string | null;
  isPdf: boolean;
}> => {
  if (isHeicLike(file)) {
    throw new Error('HEIC/HEIF photos are not supported. Save as JPG or PNG and try again.');
  }
  const isPdf = isPdfFile(file);
  // PDF pages are not rasterized here — MediaPipe needs a bitmap, so require a photo.
  if (isPdf) {
    throw new Error('Upload a JPG or PNG photo of the ID so a face can be extracted.');
  }
  const documentDataUrl = await fileToDataUrl(file);
  const faceDataUrl = await extractIdDocFaceDataUrl(documentDataUrl);
  return { documentDataUrl, faceDataUrl, isPdf: false };
};
